import { useState, useEffect } from 'react';
import { getAnimationClass } from './../utils';

export default function AssumptionsPrinciplesConstraints({ onComplete }) {
  const [showIntroP, setShowIntroP] = useState(false);
  const [fadeOutFirstSection, setFadeOutFirstSection] = useState(false);
  const [hideFirstSection, setHideFirstSection] = useState(false);

  const [showSecondP, setShowSecondP] = useState(false);
  const [showConceptCards, setShowConceptCards] = useState(false);
  const [showBottomP, setShowBottomP] = useState(false);
  const [fadeOutSecondSection, setFadeOutSecondSection] = useState(false);
  const [hideSecondSection, setHideSecondSection] = useState(false);

  const [titleIsTasks, setTitleIsTasks] = useState(false);
  const [showTasksIntro, setShowTasksIntro] = useState(false);
  const [showTasksPreview, setShowTasksPreview] = useState(false);

  useEffect(() => {
    const timeouts = [];
    let currentTime = 0;

    // Page 1 (Assumptions, Principles, Constraints intro) — visible for 8s
    currentTime += 500;
    timeouts.push(setTimeout(() => setShowIntroP(true), currentTime));

    // Page 2 starts, Page 1 fades out
    currentTime += 8000;
    timeouts.push(setTimeout(() => {
      setFadeOutFirstSection(true);
      setShowSecondP(true);
    }, currentTime));

    currentTime += 2000;
    timeouts.push(setTimeout(() => setShowConceptCards(true), currentTime));

    currentTime += 1000;
    timeouts.push(setTimeout(() => setHideFirstSection(true), currentTime));

    currentTime += 2500;
    timeouts.push(setTimeout(() => setShowBottomP(true), currentTime));

    // Page 3 starts, Page 2 fades out (Page 2 visible for 15s total)
    currentTime += 9500;
    timeouts.push(setTimeout(() => {
      setFadeOutSecondSection(true);
      setTitleIsTasks(true);
      setShowTasksIntro(true);
    }, currentTime));

    currentTime += 2000;
    timeouts.push(setTimeout(() => setShowTasksPreview(true), currentTime));

    currentTime += 1000;
    timeouts.push(setTimeout(() => setHideSecondSection(true), currentTime));

    // Page 3 ends (visible for 8s total)
    currentTime += 5000;
    timeouts.push(setTimeout(() => onComplete?.(), currentTime));

    return () => timeouts.forEach(clearTimeout);
  }, [onComplete]);

  return (
    <>
      <h1 className="animate-fade-in" key={titleIsTasks ? 'tasks' : 'apc'}>
        {titleIsTasks ? 'Your Tasks' : 'Assumptions, Principles, Constraints'}
      </h1>

      <div className="animation-section">
        {!hideFirstSection && (
          <div className={`animation-content ${fadeOutFirstSection ? 'animation-content--exiting' : ''}`}>
            <p className={getAnimationClass(showIntroP, fadeOutFirstSection)}>
              Accounting isn't just numbers. It's about telling the true story of a business. But in order for that story to be <b>reliable, comparable, and useful</b>, everyone who prepares financial information has to play by the same set of rules.
            </p>
          </div>
        )}

        {showSecondP && !hideSecondSection && (
          <div className={`animation-content ${fadeOutSecondSection ? 'animation-content--exiting' : ''}`}>
            <p className={getAnimationClass(showSecondP, fadeOutSecondSection)}>
              That's where <b>assumptions, principles, and constraints</b> come in.
            </p>
            <p className={getAnimationClass(showSecondP, fadeOutSecondSection)}>
              They are the foundation of financial reporting, the shared framework that ensures a pizzeria in New Jersey, an influencer in Los Angeles, and a fast food chain in the Midwest all record their financial activity in a way that <b>outside users</b> (investors, lenders, regulators, managers, and even the business owners themselves) can understand and trust.
            </p>

            {showConceptCards && (
              <div className={`transition-apc-concepts ${getAnimationClass(showConceptCards, fadeOutSecondSection)}`}>
                <div className="transition-apc-concept assumptions">
                  <h4>Assumptions</h4>
                  <p>The foundational beliefs we make about a business. (<b>what we accept as true</b>).</p>
                </div>
                <div className="transition-apc-concept principles">
                  <h4>Principles</h4>
                  <p>The rules and guidelines that determine how financial transactions are recorded and reported.</p>
                </div>
                <div className="transition-apc-concept constraints">
                  <h4>Constraints</h4>
                  <p>The practical limitations that influence when and how strictly accounting rules are applied.</p>
                </div>
              </div>
            )}

            {showBottomP && (
              <p className={`transition-apc-bottom-p ${getAnimationClass(showBottomP, fadeOutSecondSection)}`}>
                Without them, financial statements would not be reliable, comparable, or useful for decision-making.
              </p>
            )}
          </div>
        )}

        {titleIsTasks && (
          <div className="animation-content">
            <p className={getAnimationClass(showTasksIntro)}>
              In this exercise, you'll review different business scenarios and each will face real-world situations.
            </p>
            <p className={getAnimationClass(showTasksIntro)}>
              <b className="red">Your Task</b>: Read each activity carefully and select which principles, assumptions, and/or constraints applies. Some activities involve just one concept; others may involve two or more.
            </p>

            {showTasksPreview && (
              <div className={`transition-apc-tasks-preview ${getAnimationClass(showTasksPreview)}`} />
            )}
          </div>
        )}
      </div>
    </>
  );
}
